import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";

const TYPES = [
  { value: "note", label: "Research Note" },
  { value: "paper", label: "Position Paper" },
  { value: "observatory", label: "Observatory" },
  { value: "publication", label: "Publication" },
];

const EMPTY = { type: "note", title: "", date: "", excerpt: "", body: "", link: "", tags: [], published: true };

function ItemForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [tagsStr, setTagsStr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial } : EMPTY);
      setTagsStr(initial ? (initial.tags || []).join(", ") : "");
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/admin/research/${initial.id}`, payload);
      else await api.post("/admin/research", payload);
      toast.success(initial?.id ? "Entry updated" : "Entry created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit entry" : "New entry"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="item-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`item-type-${t.value}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input data-testid="item-title-input" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input data-testid="item-date-input" placeholder="Date (e.g. July 2026)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Textarea data-testid="item-excerpt-input" placeholder="Short excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          <Textarea data-testid="item-body-input" placeholder="Full article text (optional). Supports **bold**, *italic*, [links](https://url), ## subheadings, > quotes. Blank line = new paragraph." className="min-h-[160px]" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <Input data-testid="item-link-input" placeholder="External link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
          <Input data-testid="item-tags-input" placeholder="Tags, comma separated" value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} />
          <label className="flex items-center gap-3 text-sm">
            <Switch data-testid="item-published-switch" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            Published (visible on the site)
          </label>
          <Button data-testid="item-save-button" type="submit" disabled={saving} className="w-full">
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EVENT_EMPTY = { kind: "dialogue", title: "", date: "", location: "", description: "", rsvp_link: "", status: "upcoming", published: true };

function EventForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EVENT_EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : EVENT_EMPTY);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/admin/events/${initial.id}`, form);
      else await api.post("/admin/events", form);
      toast.success(initial?.id ? "Event updated" : "Event created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit event" : "New event"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="event-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dialogue" data-testid="event-kind-dialogue">Research Dialogue</SelectItem>
              <SelectItem value="roundtable" data-testid="event-kind-roundtable">Roundtable</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="upcoming" data-testid="event-status-upcoming">Upcoming</SelectItem>
              <SelectItem value="soon" data-testid="event-status-soon">Coming soon</SelectItem>
              <SelectItem value="past" data-testid="event-status-past">Past (archive)</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="event-title-input" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input data-testid="event-date-input" placeholder="Date (e.g. September 2026, 18:00 EET)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input data-testid="event-location-input" placeholder="Location (e.g. Online / Bucharest)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <Textarea data-testid="event-description-input" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Input data-testid="event-rsvp-input" placeholder="RSVP / Register link (optional)" value={form.rsvp_link} onChange={(e) => setForm({ ...form, rsvp_link: e.target.value })} />
          <Input data-testid="event-more-input" placeholder="Find out more link (optional)" value={form.more_link} onChange={(e) => setForm({ ...form, more_link: e.target.value })} />
          <label className="flex items-center gap-3 text-sm">
            <Switch data-testid="event-published-switch" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            Published (visible on the site)
          </label>
          <Button data-testid="event-save-button" type="submit" disabled={saving} className="w-full">
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  usePageTitle("Admin Dashboard | Mentheris");
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const load = () => {
    api.get("/admin/research").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/admin/messages").then(({ data }) => setMessages(data)).catch(() => {});
    api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/admin/newsletter").then(({ data }) => setSubscribers(data)).catch(() => {});
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (checking) return <main className="min-h-screen flex items-center justify-center text-fog text-sm">Loading…</main>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const removeItem = async (id) => {
    await api.delete(`/admin/research/${id}`);
    toast.success("Entry deleted");
    load();
  };

  const removeMessage = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    toast.success("Message deleted");
    load();
  };

  const removeEvent = async (id) => {
    await api.delete(`/admin/events/${id}`);
    toast.success("Event deleted");
    load();
  };

  const removeSubscriber = async (id) => {
    await api.delete(`/admin/newsletter/${id}`);
    toast.success("Subscriber removed");
    load();
  };

  return (
    <main className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <div className="border-b border-line bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <p className="font-serif text-xl">Mentheris<span className="text-ember">.</span> <span className="text-xs tracking-[0.2em] uppercase text-fog ml-2">CMS</span></p>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site-link" className="text-sm text-fog hover:text-ink transition-colors">View site</a>
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-2 text-sm text-fog hover:text-ember transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <Tabs defaultValue="content">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">
              Messages {messages.length > 0 && <span className="ml-2 text-xs bg-ember text-bone px-1.5 py-0.5">{messages.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers">
              Subscribers {subscribers.length > 0 && <span className="ml-2 text-xs bg-ember text-bone px-1.5 py-0.5">{subscribers.length}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-fog">{items.length} entries: notes, papers, observatory signals, publications</p>
              <Button
                data-testid="admin-new-item-button"
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                className="gap-2"
              >
                <Plus size={15} /> New entry
              </Button>
            </div>
            <div className="border border-line bg-white divide-y divide-line" data-testid="admin-items-list">
              {items.map((item) => (
                <div key={item.id} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-item-${item.id.slice(0, 8)}`}>
                  <span className="text-[10px] tracking-[0.15em] uppercase border border-line px-2 py-1 text-fog w-28 text-center shrink-0">{item.type}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-xs text-fog">{item.date} {item.published ? "· published" : "· draft"}</p>
                  </div>
                  <button data-testid={`admin-edit-${item.id.slice(0, 8)}`} onClick={() => { setEditing(item); setDialogOpen(true); }} className="p-2 text-fog hover:text-ink transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`admin-delete-${item.id.slice(0, 8)}`} onClick={() => removeItem(item.id)} className="p-2 text-fog hover:text-ember transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {items.length === 0 && <p className="px-5 py-10 text-sm text-fog">No entries yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="events" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-fog">{events.length} events: dialogues and roundtables shown on the Community page</p>
              <Button
                data-testid="admin-new-event-button"
                onClick={() => { setEditingEvent(null); setEventDialogOpen(true); }}
                className="gap-2"
              >
                <Plus size={15} /> New event
              </Button>
            </div>
            <div className="border border-line bg-white divide-y divide-line" data-testid="admin-events-list">
              {events.map((ev) => (
                <div key={ev.id} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-event-${ev.id.slice(0, 8)}`}>
                  <span className="text-[10px] tracking-[0.15em] uppercase border border-line px-2 py-1 text-fog w-28 text-center shrink-0">{ev.kind}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ev.title}</p>
                    <p className="text-xs text-fog">{ev.date}{ev.location ? ` · ${ev.location}` : ""} · {ev.status === "past" ? "past" : "upcoming"} {ev.published ? "· published" : "· draft"}</p>
                  </div>
                  <button data-testid={`admin-edit-event-${ev.id.slice(0, 8)}`} onClick={() => { setEditingEvent(ev); setEventDialogOpen(true); }} className="p-2 text-fog hover:text-ink transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`admin-delete-event-${ev.id.slice(0, 8)}`} onClick={() => removeEvent(ev.id)} className="p-2 text-fog hover:text-ember transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {events.length === 0 && <p className="px-5 py-10 text-sm text-fog">No events yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-8">
            <div className="border border-line bg-white divide-y divide-line" data-testid="admin-messages-list">
              {messages.map((m) => (
                <div key={m.id} className="px-5 py-5 flex gap-4" data-testid={`admin-message-${m.id.slice(0, 8)}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{m.name} <span className="text-fog font-normal">· {m.email}</span></p>
                    <p className="text-sm text-fog mt-1 whitespace-pre-wrap">{m.message}</p>
                    <p className="text-xs text-fog/70 mt-2">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <button data-testid={`admin-delete-message-${m.id.slice(0, 8)}`} onClick={() => removeMessage(m.id)} className="p-2 h-fit text-fog hover:text-ember transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {messages.length === 0 && <p className="px-5 py-10 text-sm text-fog">No messages yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="subscribers" className="mt-8">
            <p className="text-sm text-fog mb-6">{subscribers.length} newsletter subscribers from the Stay in touch box</p>
            <div className="border border-line bg-white divide-y divide-line" data-testid="admin-subscribers-list">
              {subscribers.map((s) => (
                <div key={s.id} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-subscriber-${s.id.slice(0, 8)}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.email}</p>
                    <p className="text-xs text-fog">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                  <button data-testid={`admin-delete-subscriber-${s.id.slice(0, 8)}`} onClick={() => removeSubscriber(s.id)} className="p-2 text-fog hover:text-ember transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {subscribers.length === 0 && <p className="px-5 py-10 text-sm text-fog">No subscribers yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ItemForm open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} onSaved={load} />
      <EventForm open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} initial={editingEvent} onSaved={load} />
    </main>
  );
}
